import { computed } from 'vue'
import { getOrCreateChatbotStore } from '@/stores/chatbot'
import { useChatbotContext } from '@/composables/useChatbotContext'
import { ChatbotApi } from '@/api'
import type { IChatbot } from '@/types'
import { DeploymentType, type Position } from '@/types/chatbot-types'
import type { ISiddDuoConfiguration } from '@/types/component-configurations'
import { CHATBOT_CONSTANTS } from '@/types/constants'
import { logger } from '@/utils/logger'

interface IUseChatbotOptions {
  chatbotId?: string
  tenantId?: string
  deploymentType?: DeploymentType
}

export function useChatbot(options: IUseChatbotOptions = {}) {
  const { deploymentType } = options
  const context = useChatbotContext()

  if (!context) {
    throw new Error(
      'useChatbot must be used within a component that has called provideChatbotContext'
    )
  }

  const scopedClient = context.apiClient
  const storeKey = context.storeKey.value!
  const storeFactory = getOrCreateChatbotStore(storeKey)
  const storeInstance = storeFactory()
  const store = storeInstance
  const isReady = computed(
    () =>
      storeInstance.isInitialized &&
      !storeInstance.isLoading &&
      storeInstance.id !== null
  )
  const hasError = computed(() => storeInstance.error !== null)

  const loadChatbot = async (): Promise<void> => {
    if (storeInstance.isLoading) return
    const chatbotId = context.chatbotId.value
    const tenantId = context.tenantId.value

    if (!chatbotId && !tenantId) {
      throw new Error(
        'loadChatbot - Either chatbotId or tenantId must be provided, or global chatbot ID must be set'
      )
    }

    storeInstance.setLoading(true)
    storeInstance.setError(null)

    try {
      let response

      if (chatbotId) {
        response = await ChatbotApi.getChatbot(chatbotId, scopedClient)
      } else if (tenantId) {
        response = await ChatbotApi.getChatbotByTenantId(
          tenantId,
          deploymentType,
          scopedClient
        )
      } else {
        throw new Error('No chatbotId or tenantId available')
      }

      if (!response.success || !response.data) {
        throw new Error(response.message || 'Failed to load chatbot')
      }

      const chatbotData: IChatbot = {
        ...response.data,
        brand: {
          brandColor:
            response.data.brand?.brandColor ?? CHATBOT_CONSTANTS.BRAND_COLOR,
          name: response.data.brand?.name ?? CHATBOT_CONSTANTS.NAME,
          description:
            response.data.brand?.description ?? CHATBOT_CONSTANTS.DESCRIPTION,
          logoUrl: response.data.brand?.logoUrl,
        },
        defaultQuestions: response.data.defaultQuestions?.length
          ? response.data.defaultQuestions
          : [],
        configuration: response.data.configuration,
      }

      storeInstance.setChatbot(chatbotData)

      if (response.data.id) {
        context.setChatbotId(response.data.id)
      }
      if (response.data.tenantId) {
        context.setTenantId(response.data.tenantId)
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to load chatbot'
      storeInstance.setError(errorMessage)
      logger.error(
        'Failed to load chatbot',
        { chatbotId: chatbotId || tenantId },
        { component: 'useChatbot' },
        error instanceof Error ? error : undefined
      )
      throw error
    } finally {
      storeInstance.setLoading(false)
    }
  }

  const refreshChatbot = async (): Promise<void> => {
    storeInstance.reset()
    await loadChatbot()
  }

  const updateChatbot = (updates: Partial<IChatbot>): void => {
    if (!storeInstance.id) return

    const currentChatbot: IChatbot = {
      id: storeInstance.id!,
      tenantId: storeInstance.tenantId!,
      createdAt: storeInstance.createdAt!,
      modifiedAt: storeInstance.modifiedAt!,
      configuration: storeInstance.configuration,
      brand: storeInstance.brand,
      defaultQuestions: storeInstance.defaultQuestions,
    }

    const updatedChatbot = { ...currentChatbot, ...updates }
    storeInstance.setChatbot(updatedChatbot)
  }

  const resetChatbot = (): void => {
    storeInstance.reset()
  }

  const applyOverrideConfiguration = (overrides: {
    position?: Position
    autoOpen?: boolean
    isActive?: boolean
  }): void => {
    storeInstance.applyOverrideConfiguration(overrides)
  }

  const setSiddDuoConfig = (config: ISiddDuoConfiguration | null): void => {
    storeInstance.setSiddDuoConfig(config)
  }

  return {
    store,
    isReady,
    hasError,
    loadChatbot,
    refreshChatbot,
    updateChatbot,
    resetChatbot,
    applyOverrideConfiguration,
    setSiddDuoConfig,
  }
}

export default useChatbot
